// 产品详情面板 - 苹果风格滑入式面板
import React, { useState, useEffect } from 'react';
import {
  X,
  Star,
  Image as ImageIcon,
  Edit2,
  Trash2,
  Copy,
  Plus,
  Save,
} from 'lucide-react';
import type { Product, ProductModel } from '@/poster/types';
import { ProductCategoryLabels } from '@/poster/types';
import { ModelInlineForm } from './ModelInlineForm';

interface ProductDetailPanelProps {
  product: Product;
  onClose: () => void;
  onEditProduct: () => void;
  onCopyProduct: () => void;
  onDeleteProduct: () => void;
  editingModel: ProductModel | 'new' | null;
  setEditingModel: (model: ProductModel | 'new' | null) => void;
  onSaveModel: (data: Partial<ProductModel>, isNew: boolean) => Promise<void>;
  onDeleteModel: (model: ProductModel) => void;
}

export function ProductDetailPanel({
  product,
  onClose,
  onEditProduct,
  onCopyProduct,
  onDeleteProduct,
  editingModel,
  setEditingModel,
  onSaveModel,
  onDeleteModel,
}: ProductDetailPanelProps) {
  // 内联编辑表单状态
  const [modelForm, setModelForm] = useState<Partial<ProductModel>>({});
  const [isSaving, setIsSaving] = useState(false);

  // 当编辑的型号改变时，初始化表单
  useEffect(() => {
    if (editingModel === 'new') {
      setModelForm({
        model_name: '',
        pitch: undefined,
        cabinet_size: '',
        weight: '',
        power_consumption: '',
        is_default: false,
      });
    } else if (editingModel) {
      setModelForm({
        model_name: editingModel.model_name,
        pitch: editingModel.pitch,
        cabinet_size: editingModel.cabinet_size || '',
        weight: editingModel.weight || '',
        power_consumption: editingModel.power_consumption || '',
        is_default: editingModel.is_default,
      });
    }
  }, [editingModel]);

  const handleSaveModel = async () => {
    if (!modelForm.model_name) return;
    setIsSaving(true);
    try {
      await onSaveModel(modelForm, editingModel === 'new');
      setEditingModel(null);
    } catch (error) {
      console.error('Error saving model:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* 遮罩层 */}
      <div 
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* 滑入式面板 */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
              {product.image_url ? (
                <img src={product.image_url} alt={product.name} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-gray-600" />
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">{product.name}</h2>
                {product.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
              </div>
              <p className="text-sm text-green-600">{product.series_name || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onEditProduct}
              className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
              title="编辑产品"
            >
              <Edit2 className="w-5 h-5 text-blue-600" />
            </button>
            <button
              onClick={onCopyProduct}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="复制产品"
            >
              <Copy className="w-5 h-5 text-gray-500" />
            </button>
            <button
              onClick={onDeleteProduct}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
              title="删除产品"
            >
              <Trash2 className="w-5 h-5 text-red-500" />
            </button>
            <div className="w-px h-6 bg-gray-200 mx-1" />
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="关闭"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Product Info */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">分类</p>
              <p className="font-medium text-sm">{ProductCategoryLabels[product.category]}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">间距范围</p>
              <p className="font-medium text-sm">{product.pitch_range || '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">工艺类型</p>
              <p className="font-medium text-sm">{product.process_type || '—'}</p>
            </div>
          </div>

          {/* Features */}
          {product.features && product.features.length > 0 && (
            <div className="mb-6">
              <p className="text-xs text-gray-500 mb-2">产品特点</p>
              <div className="flex flex-wrap gap-2">
                {product.features.map((f, i) => (
                  <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">{f}</span>
                ))}
              </div>
            </div>
          )}

          {/* Models Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">
                产品型号 ({product.models?.length || 0})
              </h3>
              {editingModel !== 'new' && (
                <button
                  onClick={() => setEditingModel('new')}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  添加型号
                </button>
              )}
            </div>

            {/* 内联添加新型号表单 */}
            {editingModel === 'new' && (
              <div className="mb-4 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                <h4 className="font-medium text-blue-900 mb-3">添加新型号</h4>
                <ModelInlineForm
                  formData={modelForm}
                  setFormData={setModelForm}
                  onSave={handleSaveModel}
                  onCancel={() => setEditingModel(null)}
                  isSaving={isSaving}
                />
              </div>
            )}

            {/* Models List */}
            {product.models && product.models.length > 0 ? (
              <div className="space-y-3">
                {product.models.map((model) => (
                  <div key={model.id}>
                    {editingModel && typeof editingModel === 'object' && editingModel.id === model.id ? (
                      // 内联编辑表单
                      <div className="p-4 bg-amber-50 rounded-xl border-2 border-amber-200">
                        <h4 className="font-medium text-amber-900 mb-3">编辑型号: {model.model_name}</h4>
                        <ModelInlineForm
                          formData={modelForm}
                          setFormData={setModelForm}
                          onSave={handleSaveModel}
                          onCancel={() => setEditingModel(null)}
                          isSaving={isSaving}
                        />
                      </div>
                    ) : (
                      // 型号卡片
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 hover:border-gray-300 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold text-gray-900">{model.model_name}</span>
                              {model.is_default && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">默认</span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600">
                              {model.pitch && (
                                <p>间距: <span className="text-gray-900">P{model.pitch}</span></p>
                              )}
                              {model.cabinet_size && (
                                <p>箱体: <span className="text-gray-900">{model.cabinet_size}</span></p>
                              )}
                              {model.weight && (
                                <p>重量: <span className="text-gray-900">{model.weight}</span></p>
                              )}
                              {model.power_consumption && (
                                <p>功耗: <span className="text-gray-900">{model.power_consumption}</span></p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-4">
                            <button
                              onClick={() => setEditingModel(model)}
                              className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors"
                              title="编辑"
                            >
                              <Edit2 className="w-4 h-4 text-blue-600" />
                            </button>
                            <button
                              onClick={() => onDeleteModel(model)}
                              className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                              title="删除"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : editingModel !== 'new' && (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <p className="text-gray-400 mb-2">暂无型号数据</p>
                <button
                  onClick={() => setEditingModel('new')}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  添加第一个型号
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 滑入动画 CSS */}
      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
